import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Modal,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../axiosInstance";
import {
  BUTTON_STYLES,
  FORM_STYLES,
  MODAL_STYLES,
  TEXT_FIELD_STYLES,
} from "../../styles/componentStyles";
import {
  EMPTY_SELECTION,
  courseOptions,
  fromSaved,
  regulationOptions,
  semesterOptions,
  toPayload,
  toggle,
  universityOptions,
} from "./comboAcademic";

// The four levels, in order. `level` is the selection key in comboAcademic.js.
const LEVELS = [
  { level: "universityIds", label: "University", choices: universityOptions, hint: "Select one or more." },
  { level: "courseIds", label: "Course", choices: courseOptions, hint: "None selected = every course of the selected universities." },
  { level: "regulationNames", label: "Regulation", choices: regulationOptions, hint: "None selected = every regulation of the selected courses." },
  { level: "semesterIds", label: "Semester", choices: semesterOptions, hint: "None selected = every semester of the selected regulations." },
];

const sameIds = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);
const sortedIds = (ids) => [...new Set(ids || [])].sort((x, y) => x - y);

// Which students a combo discount applies to (University -> Course -> Regulation
// -> Semester). Opened from the mapping icon in the Combo Discounts list, like
// Banner Course Mapping; saving changes only the mapping, never the percentages.
function ComboDiscountMappingModal({ open, onClose, comboMap, onSuccess }) {
  const isEdit = (comboMap?.mappedCombinations ?? 0) > 0;
  // Valid combinations (GET .../academic-options); null while loading, "error" if they failed.
  const [options, setOptions] = useState(null);
  const [selection, setSelection] = useState(EMPTY_SELECTION);
  const [inexact, setInexact] = useState(false);
  const [saving, setSaving] = useState(false);
  // The backend's reason a save was refused (e.g. an overlap with another combo discount).
  const [saveError, setSaveError] = useState(null);

  // Load the options, then start from the saved mapping (edit) or nothing (add)
  // in the same update, so the form never shows the options without the saved ticks.
  useEffect(() => {
    if (!open || !comboMap) return undefined;
    let active = true;
    setOptions(null);
    setSelection(EMPTY_SELECTION);
    setSaveError(null);
    api
      .get("/api/combo-discount-prices/academic-options")
      .then((res) => {
        if (!active) return;
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        const saved = fromSaved(list, comboMap);
        // The saved mapping can only be shown as the cross-product of these levels;
        // if reading it back would cover something else, say so instead of hiding it.
        const back = toPayload(list, saved);
        setInexact(
          (comboMap.mappedCombinations ?? 0) > 0 &&
            !(
              sameIds(back.universityIds, sortedIds(comboMap.universityIds)) &&
              sameIds(back.courseIds, sortedIds(comboMap.courseIds)) &&
              sameIds(back.regulationYearIds, sortedIds(comboMap.regulationYearIds)) &&
              sameIds(back.semesterIds, sortedIds(comboMap.semesterIds))
            ),
        );
        setSelection(saved);
        setOptions(list);
      })
      .catch((error) => {
        console.error("Failed to load academic options:", error);
        if (active) setOptions("error");
      });
    return () => {
      active = false;
    };
  }, [open, comboMap]);

  const loaded = Array.isArray(options);
  const choices = useMemo(
    () =>
      Object.fromEntries(LEVELS.map((l) => [l.level, loaded ? l.choices(options, selection) : []])),
    [loaded, options, selection],
  );
  const flip = (level, value) => {
    setSaveError(null);
    setSelection((prev) => toggle(options, prev, level, value));
  };
  // A multi-select reports the whole new list; apply it as one toggle per change.
  const onSelectChange = (level) => (event) => {
    const next = event.target.value;
    const current = selection[level];
    const changed = next.find((v) => !current.includes(v)) ?? current.find((v) => !next.includes(v));
    if (changed !== undefined) flip(level, changed);
  };

  const handleSave = async () => {
    if (!loaded) return;
    setSaving(true);
    try {
      const response = await api.put(
        `/api/combo-discount-prices/${comboMap.id}/academic-mapping`,
        toPayload(options, selection),
      );
      // Latest mapping wins: say which discounts lost combinations to this one.
      const moved = Object.entries(response.data.data?.transferredFrom || {});
      const movedText = moved.length
        ? ` Moved here from ${moved.map(([id, n]) => `Combo Discount ID ${id} (${n})`).join(", ")}.`
        : "";
      toast.success(`Academic mapping saved successfully!${movedText}`, {
        position: "top-right",
        autoClose: moved.length ? 5000 : 2000,
      });
      onSuccess(response.data.data);
      onClose();
    } catch (error) {
      const message = error.response?.data?.errorMessage || "Failed to save the mapping. Please try again.";
      setSaveError(message);
      toast.error(message, { autoClose: 4000 });
      console.error("API Error:", error);
    } finally {
      setSaving(false);
    }
  };

  const noUniversity = selection.universityIds.length === 0;

  return (
    <Modal open={open} onClose={onClose} disableScrollLock>
      <Box sx={{ ...MODAL_STYLES.standard, width: { xs: 340, md: 500 }, maxHeight: "90vh", overflowY: "auto" }}>
        <Typography variant="h5" component="h2" sx={{ mb: 0.5, fontWeight: 600 }}>
          {isEdit ? "Edit Academic Mapping" : "Add Academic Mapping"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Combo Discount Academic Mapping: the students (by university, course,
          regulation and semester) this discount applies to. With no university
          selected it applies to no one. A combination already mapped to another
          combo discount moves to this one when you save.
        </Typography>

        <Box sx={FORM_STYLES.container}>
          <TextField
            label="Combo Discount ID"
            value={comboMap?.id ?? ""}
            InputProps={{ readOnly: true }}
            {...TEXT_FIELD_STYLES.standard}
          />

          {options === null && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {options === "error" && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Couldn't load universities and courses. Close and try again.
            </Alert>
          )}
          {inexact && (
            <Alert severity="info" sx={{ mt: 2 }}>
              The saved mapping can't be shown exactly with these filters. Saving
              replaces it with the selection below.
            </Alert>
          )}

          {loaded &&
            LEVELS.map(({ level, label, hint }) => {
              const list = choices[level];
              const selected = selection[level];
              const disabled = level !== "universityIds" && noUniversity;
              const name = (id) => list.find((c) => c.id === id)?.name ?? id;
              const fieldLabel = selected.length ? `${label} (${selected.length} selected)` : label;
              return (
                <FormControl key={level} fullWidth sx={{ mt: 2 }} disabled={disabled}>
                  <InputLabel id={`${level}-label`}>{fieldLabel}</InputLabel>
                  <Select
                    labelId={`${level}-label`}
                    multiple
                    value={selected}
                    onChange={onSelectChange(level)}
                    input={<OutlinedInput label={fieldLabel} />}
                    renderValue={(values) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {values.map((id) => (
                          <Chip
                            key={id}
                            label={name(id)}
                            size="small"
                            onDelete={() => flip(level, id)}
                            onMouseDown={(event) => event.stopPropagation()}
                          />
                        ))}
                      </Box>
                    )}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                  >
                    {list.map((c) => (
                      <MenuItem key={c.id} value={c.id} dense>
                        <Checkbox size="small" checked={selected.includes(c.id)} />
                        <ListItemText primary={c.name} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{disabled ? "Select a university first." : hint}</FormHelperText>
                </FormControl>
              );
            })}

          {saveError && (
            <Alert severity="error" sx={{ mt: 2 }} role="alert">
              {saveError}
            </Alert>
          )}

          <Box
            sx={FORM_STYLES.actions}
            style={{ display: "flex", marginTop: "20px", justifyContent: "flex-end", gap: "10px" }}
          >
            <Button onClick={onClose} {...BUTTON_STYLES.secondary}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!loaded || saving} {...BUTTON_STYLES.primary}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

export default ComboDiscountMappingModal;
